import { Star, Trash2, Bell, ChevronRight, BellRing, Newspaper, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useScoresPath, useScoresNavigate } from './scoresPath';
import { useState } from 'react';
import { toast } from 'sonner';
import { useFavoriteTeams, useFavoriteMatches, readStoredTeamInfo, readStoredMatchInfo } from './useScoresFavorites';
import { syncScoresPushPreferences, usePushNotifications } from './usePushNotifications';

/**
 * Favorites page — shows the user's starred teams and individual matches
 * they've opted into push alerts for. All client-side (localStorage).
 *
 * Empty-state has a friendly hint pointing back to the matches screen.
 */
const ScoresFavorites = () => {
  const navigate = useScoresNavigate();
  const homePath = useScoresPath('');
  const favTeams = useFavoriteTeams();
  const favMatches = useFavoriteMatches();
  const teamInfo = readStoredTeamInfo();
  const push = usePushNotifications();

  // Major news push-alert preferences — saved client-side; the actual
  // delivery wires up to Capacitor APNs/FCM when the native build ships.
  const [newsAlerts, setNewsAlerts] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem('sk_scores_news_alerts') === '1';
  });
  const toggleNewsAlerts = () => {
    const next = !newsAlerts;
    setNewsAlerts(next);
    try { localStorage.setItem('sk_scores_news_alerts', next ? '1' : '0'); } catch {}
    if (next) {
      // Trigger the native APNs/FCM permission flow on first opt-in. On web
      // we fall back to the browser Notification API so we still hint to
      // users that permission matters.
      if (push.supported) {
        push.request();
      } else if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        Notification.requestPermission().catch(() => {});
      }
      toast.success('Major news alerts on', { description: 'You\'ll be the first to know about transfers, results & breaking stories.' });
    } else {
      toast('Major news alerts off');
    }
    // Push the preference up so the backend cron knows who to alert.
    syncScoresPushPreferences({ major_news_alerts: next });
  };

  // Whenever favorites change, sync the latest sets to the backend so the
  // push dispatcher knows which teams + matches to target. (Sync logic
  // lives in ScoresApp.js so it fires from any page, not just /favorites.)

  const teamList = Array.from(favTeams.teams)
    .map((id) => teamInfo[id] || { id, name: `Team #${id}` })
    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const matchInfo = readStoredMatchInfo();
  const matchList = Array.from(favMatches.matches)
    .map((fid) => matchInfo[fid] || { fixture_id: fid })
    .sort((a, b) => new Date(a.date || 0) - new Date(b.date || 0));

  const isEmpty = teamList.length === 0 && matchList.length === 0;

  // Personalised "alerts control panel" summary — visible whenever any
  // notification channel is active. Shows users exactly what they're
  // subscribed to and gently nudges them to add more.
  const activeChannels = (newsAlerts ? 1 : 0) + (teamList.length > 0 ? 1 : 0) + (matchList.length > 0 ? 1 : 0);
  const summaryBits = [];
  if (newsAlerts) summaryBits.push('Major news');
  if (teamList.length > 0) summaryBits.push(`${teamList.length} team${teamList.length === 1 ? '' : 's'}`);
  if (matchList.length > 0) summaryBits.push(`${matchList.length} match${matchList.length === 1 ? '' : 'es'}`);
  const summaryText = summaryBits.join(' · ');

  return (
    <div className="px-4 py-4" data-testid="scores-favorites-page">
      <h1 className="text-xl font-black mb-1">Favorites</h1>
      <p className="text-xs text-zinc-500 mb-4">Star teams to pin them at the top. Toggle the bell on any match for live alerts.</p>

      {/* Active subscriptions banner — only shows when the user has at least
          one alert channel on. Acts as a personal control panel so people
          know what they'll be pinged about. */}
      {activeChannels > 0 && (
        <div
          className="flex items-center gap-3 p-3 rounded-2xl mb-4 bg-gradient-to-br from-accent/15 via-accent/8 to-transparent border border-[color:var(--sk-accent-border)]"
          data-testid="alerts-summary-banner"
        >
          <div className="shrink-0 w-9 h-9 rounded-xl bg-accent/20 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-accent" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-black uppercase tracking-wider text-accent mb-0.5">Alerts on</div>
            <div className="text-[12px] text-white truncate" data-testid="alerts-summary-text">{summaryText}</div>
          </div>
          {activeChannels < 3 && (
            <div className="text-[10px] text-zinc-500 shrink-0">{activeChannels}/3</div>
          )}
        </div>
      )}

      {/* Major News Alerts — opt-in */}
      <button
        type="button"
        onClick={toggleNewsAlerts}
        data-testid="news-alerts-toggle"
        className={`w-full flex items-center gap-3 p-3 rounded-2xl mb-5 transition-colors ${newsAlerts ? 'bg-accent-soft border border-[color:var(--sk-accent-border)]' : 'bg-[var(--sk-surface)] border border-[var(--sk-border)] hover:bg-white/[0.03]'} text-left`}
      >
        <div className={`shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${newsAlerts ? 'bg-accent text-white' : 'bg-white/5 text-zinc-400'}`}>
          {newsAlerts ? <BellRing className="w-5 h-5" /> : <Newspaper className="w-5 h-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-bold text-white">Major Football News Alerts</div>
          <div className="text-[11px] text-zinc-400 mt-0.5 leading-snug">
            Get notified about transfers, breaking stories, results & big-match previews — Premier League, La Liga, Serie A, Champions League & more.
          </div>
        </div>
        <div className={`shrink-0 w-9 h-5 rounded-full p-0.5 transition-colors ${newsAlerts ? 'bg-accent' : 'bg-white/10'}`}>
          <div className={`w-4 h-4 rounded-full bg-white transition-transform ${newsAlerts ? 'translate-x-4' : 'translate-x-0'}`} />
        </div>
      </button>

      {isEmpty ? (
        <div className="rounded-2xl border border-dashed border-[var(--sk-border)] p-8 text-center" data-testid="favorites-empty-state">
          <div className="w-12 h-12 mx-auto rounded-full bg-accent-soft flex items-center justify-center mb-3">
            <Star className="w-5 h-5 text-accent" />
          </div>
          <div className="text-sm font-bold mb-1">No favorites yet</div>
          <p className="text-xs text-zinc-500 mb-4">Tap the ☆ next to any team on the Matches page to add them here.</p>
          <Link
            to={homePath}
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-accent text-white text-xs font-bold"
            data-testid="favorites-cta-matches"
          >
            Browse matches <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {teamList.length > 0 && (
            <section>
              <div className="flex items-center gap-1.5 mb-2">
                <Star className="w-3.5 h-3.5 text-accent" fill="currentColor" />
                <h2 className="text-[11px] font-black uppercase tracking-wider text-zinc-400">Teams ({teamList.length})</h2>
              </div>
              <div className="space-y-1.5">
                {teamList.map((t) => (
                  <div
                    key={t.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-[var(--sk-surface)]"
                    data-testid={`fav-team-row-${t.id}`}
                  >
                    {t.logo ? (
                      <img src={t.logo} alt="" className="w-6 h-6 object-contain" loading="lazy" />
                    ) : (
                      <div className="w-6 h-6 rounded bg-white/5" />
                    )}
                    <button
                      onClick={() => navigate(`/team/${t.id}`)}
                      className="flex-1 text-left text-sm font-medium truncate hover:text-accent"
                      data-testid={`fav-team-open-${t.id}`}
                    >
                      {t.name}
                    </button>
                    <button
                      onClick={() => favTeams.toggle({ id: t.id, name: t.name, logo: t.logo })}
                      className="w-8 h-8 rounded-full hover:bg-white/5 flex items-center justify-center text-zinc-500 hover:text-rose-400 transition-colors"
                      aria-label={`Remove ${t.name}`}
                      data-testid={`fav-team-remove-${t.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          )}

          {matchList.length > 0 && (
            <section>
              <div className="flex items-center gap-1.5 mb-2">
                <Bell className="w-3.5 h-3.5 text-accent" />
                <h2 className="text-[11px] font-black uppercase tracking-wider text-zinc-400">Match Alerts ({matchList.length})</h2>
              </div>
              <div className="space-y-1.5" data-testid="fav-match-list">
                {matchList.map((mt) => {
                  const d = mt.date ? new Date(mt.date) : null;
                  const valid = d && !isNaN(d.getTime());
                  const dayLabel = valid ? d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '';
                  const timeLabel = valid ? d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : 'TBD';
                  return (
                    <div
                      key={mt.fixture_id}
                      className="flex items-center gap-2 px-3 py-2.5 rounded-xl bg-[var(--sk-surface)]"
                      data-testid={`fav-match-row-${mt.fixture_id}`}
                    >
                      <button
                        onClick={() => navigate(`/match/${mt.fixture_id}`)}
                        className="flex-1 min-w-0 flex items-center gap-3 text-left"
                        data-testid={`fav-match-open-${mt.fixture_id}`}
                      >
                        <div className="shrink-0 w-11 text-center leading-tight">
                          <div className="text-[10px] font-bold text-zinc-500">{dayLabel}</div>
                          <div className="text-[12px] font-bold text-white tabular-nums">{timeLabel}</div>
                        </div>
                        <div className="flex-1 min-w-0 space-y-1">
                          <div className="flex items-center gap-2">
                            {mt.home_logo ? (
                              <img src={mt.home_logo} alt="" className="w-4 h-4 object-contain shrink-0" loading="lazy" />
                            ) : (
                              <div className="w-4 h-4 rounded bg-white/5 shrink-0" />
                            )}
                            <span className="text-[13px] text-white truncate">{mt.home_team || 'Home'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {mt.away_logo ? (
                              <img src={mt.away_logo} alt="" className="w-4 h-4 object-contain shrink-0" loading="lazy" />
                            ) : (
                              <div className="w-4 h-4 rounded bg-white/5 shrink-0" />
                            )}
                            <span className="text-[13px] text-white truncate">{mt.away_team || 'Away'}</span>
                          </div>
                        </div>
                      </button>
                      <button
                        onClick={() => favMatches.toggle(mt.fixture_id)}
                        className="shrink-0 w-9 h-9 rounded-full bg-accent-soft text-accent flex items-center justify-center hover:bg-white/5 hover:text-rose-400 transition-colors"
                        aria-label="Remove match alert"
                        data-testid={`fav-match-remove-${mt.fixture_id}`}
                      >
                        <BellRing className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
                <p className="text-[11px] text-zinc-500 px-1 pt-1">
                  You'll get notified on kick-off, goals and the final score. Tap the bell to remove.
                </p>
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
};

export default ScoresFavorites;
